import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { USER_ROLE_ADMIN, USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';

import SrH1 from 'client/components/SrH1';
import SrPage from 'client/components/SrPage';
import ButtonDeprecated, { BUTTON_THEME_YELLOW } from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';

import {
  SR_API_ACTION_ADMIN_GET_USERS_WITH_ROLE,
  SR_API_ACTION_APARTMENT_LOCATOR_GET_COMMISSION_STRUCTURE,
  SR_API_ACTION_APARTMENT_LOCATOR_ASSIGN_COMMISSION_STRUCTURE,
} from 'api/actionTypes';

import { SCREEN_ID_ADMIN_FINANCE_LOCATOR_COMMISSION_STRUCTURES } from 'client/constants/screenIds';

import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  getLocatorsListApiState: {
    action: SR_API_ACTION_ADMIN_GET_USERS_WITH_ROLE,
    queryId: USER_ROLE_APARTMENT_LOCATOR,
  },
  getLocatorCommissionStructureApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_GET_COMMISSION_STRUCTURE,
  },
  assignLocatorCommissionStructureApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_ASSIGN_COMMISSION_STRUCTURE,
  },
})
@withScreenId(SCREEN_ID_ADMIN_FINANCE_LOCATOR_COMMISSION_STRUCTURES)
@branch({
  apartmentLocators: ['apartmentLocators'],
})
class FinancesLocatorCommissionStructuresPage extends Component {
  static fetchData(match, inject) {
    return ApartmentLocatorActions.getApartmentLocatorsList(inject);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Locator Commission Structures | Sunroom Rentals',
  });

  static propTypes = {
    apartmentLocators: PropTypes.array,
    getLocatorsListApiState: PropTypes.object.isRequired,
    getLocatorCommissionStructureApiState: PropTypes.object.isRequired,
    assignLocatorCommissionStructureApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedLocatorId: null,
      currentCommissionStructure: null,
      newCommissionStructureFormActive: false,
    };
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onLocatorSelected = (selectedLocatorId) => {
    const { dispatch } = this.props;
    this.setState({
      selectedLocatorId: selectedLocatorId || null,
      currentCommissionStructure: null,
      newCommissionStructureFormActive: false,
    });
    if (selectedLocatorId) {
      dispatch(postWithState, {
        action: SR_API_ACTION_APARTMENT_LOCATOR_GET_COMMISSION_STRUCTURE,
        payload: {
          locatorId: selectedLocatorId,
        },
      }).then((commissionStructure) => {
        if (!this._isUnmounting) {
          this.setState({
            currentCommissionStructure: commissionStructure,
          });
        }
      });
    }
  };

  _onOpenNewCommissionStructureForm = () => {
    this.setState({
      newCommissionStructureFormActive: true,
    });
  };

  _onCommissionStructureSubmit = ({ commissionStructureJson }) => {
    const { dispatch } = this.props;
    const { selectedLocatorId } = this.state;
    const commissionStructure = JSON.parse(commissionStructureJson);
    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_LOCATOR_ASSIGN_COMMISSION_STRUCTURE,
      payload: {
        locatorId: selectedLocatorId,
        commissionStructure: commissionStructure,
      },
    }).then(() => {
      return this._onLocatorSelected(selectedLocatorId);
    });
  };

  render() {
    const {
      apartmentLocators,
      getLocatorsListApiState,
      getLocatorCommissionStructureApiState,
      assignLocatorCommissionStructureApiState,
    } = this.props;

    const {
      selectedLocatorId,
      currentCommissionStructure,
      newCommissionStructureFormActive,
    } = this.state;

    return (
      <SrPage
        navbarProps={{
          redirectLogoTo: '/admin',
          backButtonLink: '/admin',
        }}
      >
        <SrH1>apartment locator commission structures</SrH1>

        {assignLocatorCommissionStructureApiState.error && (
          <div>{assignLocatorCommissionStructureApiState.error.message}</div>
        )}
        {!apartmentLocators || getLocatorsListApiState.inProgress ? (
          <div className="loading-gif" />
        ) : (
          <select
            onChange={(event) =>
              this._onLocatorSelected(event.target.value ? parseInt(event.target.value, 10) : null)
            }
            value={selectedLocatorId || ''}
            className="dropdown"
          >
            <option value="">choose locator</option>
            {apartmentLocators.map((locator) => (
              <option key={locator.id} value={locator.id}>
                {locator.name}
              </option>
            ))}
          </select>
        )}
        {selectedLocatorId &&
          (getLocatorCommissionStructureApiState.inProgress ? (
            <div className="loading-gif" />
          ) : !currentCommissionStructure ? (
            <div className="message emptyState">
              <span>Locator's commission structure hasn't been set up.</span>
            </div>
          ) : (
            <pre>{JSON.stringify(currentCommissionStructure, null, 2)}</pre>
          ))}
        {selectedLocatorId &&
          (!getLocatorCommissionStructureApiState.inProgress &&
          !newCommissionStructureFormActive ? (
            <button
              className="button blue"
              type="button"
              onClick={this._onOpenNewCommissionStructureForm}
            >
              update commission structure
            </button>
          ) : (
            <div className="formContainer">
              <Formik
                initialValues={{ commissionStructureJson: '' }}
                onSubmit={this._onCommissionStructureSubmit}
                render={(formikBag) => (
                  <Form>
                    <SrFormikField
                      formField={{
                        id: 'commissionStructureJson',
                        title: 'Commission Structure JSON',
                        label:
                          'Example JSON: [' +
                          '{"from": 0, "to": 5000, "locatorCut": 0.5 }, ' +
                          '{"from": 5000, "to": 10000, "locatorCut": 0.6 }, ' +
                          '{"from": 10000, "locatorCut": 0.7}' +
                          ']',
                        properties: {
                          placeholder:
                            '[' +
                            '{"from": 0, "to": 5000, "locatorCut": 0.5 }, ' +
                            '{"from": 5000, "to": 10000, "locatorCut": 0.6 }, ' +
                            '{"from": 10000, "locatorCut": 0.7}' +
                            ']',
                        },
                        type: 'long_text',
                      }}
                      formikBag={formikBag}
                    />
                    <ButtonDeprecated
                      theme={BUTTON_THEME_YELLOW}
                      type="submit"
                      disabled={assignLocatorCommissionStructureApiState.inProgress}
                    >
                      submit
                    </ButtonDeprecated>
                  </Form>
                )}
              />
            </div>
          ))}
      </SrPage>
    );
  }
}

FinancesLocatorCommissionStructuresPage.displayName = 'FinancesLocatorCommissionStructuresPage';

export default FinancesLocatorCommissionStructuresPage;
